/* eslint-disable prefer-const */
/* eslint-disable no-shadow */
import * as d3 from 'd3'
import config from '@/config.json'

const sortElementsForDraw = (boardElements, linkElements) => {
  if (boardElements.length <= 0) return []
  let elements = boardElements
  if (linkElements.length > 0) {
    let currentIndex = 0
    let lastTrackIndex = 0
    for (let element of boardElements) {
      if (!element.isTrackElement) {
        lastTrackIndex = currentIndex
        break
      }
      currentIndex += 1
    }
    elements = [...elements.slice(0, lastTrackIndex), ...linkElements, ...elements.slice(lastTrackIndex, elements.length)]
  }
  return elements
}

const getDefaultState = () => ({
  mapD3: {},
  map: {},
  ctx: {},
  zoom: {},
  zoomLevel: 1.0,
  transform: d3.zoomIdentity,
  areLinksVisible: true,
  areZonesVisible: false,
  testM: 'map'
})

const state = getDefaultState()
const getters = {
  mapD3: (state) => state.mapD3,
  map: (state) => state.map,
  ctx: (state) => state.ctx,
  zoom: (state) => state.zoom,
  zoomLevel: (state) => state.zoomLevel,
  transform: (state) => state.transform,
  scale: (state, getters) => {
    const boardXDomain = getters.dimensions.level.width
    const boardXRange = getters.dimensions.board.width
    const boardYDomain = getters.dimensions.level.height
    const boardYRange = getters.dimensions.board.height
    const mapHeight = getters.mapHeight
    const mapWidth = getters.mapWidth

    // TODO: gui domain dynamically
    const guiXDomain = 100
    const guiYDomain = 100
    return {
      board: {
        y: d3.scaleLinear().domain([0, boardYDomain]).range([0, boardYRange]),
        x: d3.scaleLinear().domain([0, boardXDomain]).range([0, boardXRange])
      },
      gui: {
        y: d3.scaleLinear().domain([0, guiYDomain]).range([0, getters.mapHeight]),
        x: d3.scaleLinear().domain([0, guiXDomain]).range([0, getters.mapWidth]),
      }
    }
  },
  getCellFromCoords: (state, getters) => (mouseY, mouseX) => {
    let y = mouseY
    let x = mouseX
    // Transform and scale mouse coordinates to board cell
    x -= getters.transform.x
    y -= getters.transform.y
    x /= getters.transform.k
    y /= getters.transform.k
    const [newY, newX] = [getters.scale.board.y.invert(y - 50), getters.scale.board.x.invert(x - 50)]
    const snappedY = Math.round(newY)
    const snappedX = Math.round(newX)
    return [snappedY, snappedX]
  },
  isOnTrack: (state, getters) => (mouseY, mouseX) => {
    const cell = getters.getCellFromCoords(mouseY, mouseX)

    // Check if mouse board cell matches any board cells of tracks
    let flag = false
    getters.boardTrackElements.forEach((te) => {
      if (te.cell[0] === cell[0] && te.cell[1] === cell[1]) {
        flag = true
      }
    })
    return flag
  },
  isTrackOccupied: (state, getters) => (draggedEl, y, x) => {
    let flag = false
    let elements = getters.boardImageElements
    // If draggedElement is from board (not toolbar) then it must be removed from elements being considered
    if (draggedEl.isBoardElement) {
      elements = elements.filter((el) => el.id !== draggedEl.id)
    }
    const cell = getters.getCellFromCoords(y, x)

    elements.forEach((be) => {
      if (be.cell[0] === cell[0] && be.cell[1] === cell[1]) {
        flag = true
      }
    })
    return flag
  },
  areLinksVisible: (state) => state.areLinksVisible,
  areZonesVisible: (state) => state.areZonesVisible,
  testM: (state) => state.testM
}
const actions = {
  initMap: async ({ dispatch, getters }, payload) => {
    dispatch('setMapComponents', payload)
    await dispatch('bindMapFeaturesToElements')
    // Do not enable zoom, primarily when used in LevelSelect
    if (!getters.isZoomDisabled) {
      await dispatch('initZoom')
    }
    dispatch('callZoomOffset')
    dispatch('draw')
  },
  setMapComponents: ({ commit }, { mapD3, map, ctx, mapCtr }) => {
    commit('setMapD3', mapD3)
    commit('setMap', map)
    commit('setCtx', ctx)
    commit('setMapCtr', mapCtr)
  },
  bindMapFeaturesToElements: async ({ dispatch, getters }) => {
    await dispatch('bindScale', getters.scale)
    await dispatch('bindTransform', getters.transform)
  },
  initZoom: async ({ commit, dispatch }) => {
    console.log('initZoom')
    // This is the only mouse event in the system that is not controlled by the canvas itself in Map.vue
    const zoom = d3.zoom()
      .filter(() => {
        // Map drag events must be accompanied with shift key
        if (d3.event.type === 'mousedown') {
          return d3.event.shiftKey
        }
        return true
      })
      .scaleExtent([0.5, 5])
      .on('zoom', (() => {
        dispatch('draw')
        commit('setZoomLevel', d3.event.transform.k)
      }))
    commit('setZoom', zoom)
    if (state.mapD3._groups) {
      state.mapD3.call(zoom)
      // Prevent double clicking
      state.mapD3.on('dblclick.zoom', null)
    }
  },
  callZoomOffset: ({ getters, dispatch }) => {
    let xOffset
    let yOffset
    const boardWidth = getters.dimensions.board.width
    const boardHeight = getters.dimensions.board.height
    const mapWidth = getters.mapWidth
    const mapHeight = getters.mapHeight

    const scaleX = mapWidth / boardWidth
    const scaleY = mapHeight / boardHeight

    // too keep maps scaled evenly, use smaller scale
    // scale is reduced slightly further to provide a buffer for non board elements
    const scale = Math.min(scaleX, scaleY) * 0.8

    xOffset = ((mapWidth - boardWidth * scale)) / 2
    yOffset = ((mapHeight - boardHeight * scale)) / 2

    dispatch('setTransform', d3.zoomIdentity.translate(xOffset, yOffset).scale(scale, scale).translate(0, 0))
    // Calling right away prevents jumping on first zoom
    if (!getters.isZoomDisabled && state.mapD3._groups) {
      getters.mapD3.call(getters.zoom.transform, getters.transform)
    }
  },
  draw: ({ getters, dispatch }) => {
    const ctx = getters.ctx
    if (ctx.fillStyle) {
      ctx.clearRect(0, 0, getters.mapWidth, getters.mapHeight)
      if (!getters.transform) {
        console.error('Transform must be set before initial draw')
      }
    // Check for zoom event
      if (d3.event) {
        dispatch('setTransform', d3.event.transform)
      }
      const { y, x, k } = getters.transform
      ctx.save()
      ctx.translate(x, y)
      ctx.scale(k, k)

      // Specifically draw tracks first, then draw the links, finally draw other elements
      let drewLinks = false
      let boardElements = getters.boardElements
      let linkElements = getters.linkElements
      for (let i = 0; i < boardElements.length; i++) {
        // console.log(`mI:${i}`)
        if (!boardElements[i].isTrackElement && !drewLinks && getters.areLinksVisible) {
          for (let j = 0; j < linkElements.length; j++) {
            // console.log(`mJ:${j}`)
            linkElements[j].draw(ctx)
            // draw hint
          }
          drewLinks = true
        }
        boardElements[i].draw(ctx)
      }

      let zoneElements = getters.zoneElements
      for (let i = 0; i < zoneElements.length; i++) {
        if (getters.areZonesVisible) {
          zoneElements[i].draw(ctx)
        }
      }

      // const elements = sortElementsForDraw(getters.boardElements, getters.linkElements)
      // elements.forEach((el) => el.draw(ctx))

      ctx.restore()
      // GUI elements don't need to be scaled
      getters.guiElements.forEach((el) => el.draw(ctx))
    }
  },
  setTransform: ({ dispatch, commit }, transform) => {
    commit('setTransform', transform)
    dispatch('bindTransform', transform)
  },
  setZoomLevel: ({ dispatch, commit }, zoomLevel) => {
    commit('setZoomLevel', zoomLevel)
  },
  updateMapElements: async ({ getters, dispatch }, { width, height }) => {
    await dispatch('setMapSize', { width, height })
    await dispatch('bindScale', getters.scale)
    dispatch('draw')
  },
  toggleLinksVisibility: ({ commit }, flag) => {
    commit('toggleLinksVisibility', flag)
  },
  toggleZonesVisibility: ({ commit }, flag) => {
    commit('toggleZonesVisibility', flag)
  },
  destroyMap({ commit }) {
    commit('destroyMap')
  },
  setTestM: ({ commit }, value) => {
    commit('setTestM', value)
  }
}

const mutations = {
  setMapD3(state, mapD3) {
    state.mapD3 = mapD3
  },
  setMapCtr(state, mapCtr) {
    state.mapCtr = mapCtr
  },
  setMap(state, map) {
    state.map = map
  },
  setCtx(state, ctx) {
    state.ctx = ctx
  },
  setZoom(state, zoom) {
    state.zoom = zoom
  },
  setZoomLevel(state, zoomLevel) {
    state.zoomLevel = zoomLevel
  },
  setTransform(state, transform) {
    state.transform = transform
  },
  toggleLinksVisibility(state, flag) {
    state.areLinksVisible = flag || !state.areLinksVisible
  },
  toggleZonesVisibility(state, flag) {
    state.areZonesVisible = flag || !state.areZonesVisible
  },
  destroyMap(state) {
    Object.assign(state, getDefaultState())
  },
  setTestM: (state, value) => {
    state.testM = value
  }
}

export default {
  state,
  getters,
  actions,
  mutations,
  sortElementsForDraw
}
